import React, { useState, useEffect, useContext } from 'react';
import { Context } from '../context';
import {
    Button,
    Input,
    Select,
    Label,
    Card,
    CardHeader,
    CardPreview,
    Badge,
    makeStyles,
    shorthands,
    tokens,
    Switch,
    Dropdown,
    Option,
} from "@fluentui/react-components";
import {
    FilterRegular,
    ImageRegular,
    EyeRegular,
    SearchRegular,
} from "@fluentui/react-icons";

const useStyles = makeStyles({
    root: {
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        ...shorthands.padding("20px"),
        ...shorthands.gap("20px"),
    },
    controls: {
        display: "flex",
        flexWrap: "wrap",
        ...shorthands.gap("15px"),
        ...shorthands.padding("15px"),
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusMedium,
        alignItems: "center",
    },
    gallery: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
        ...shorthands.gap("20px"),
        overflowY: "auto",
        flex: 1,
    },
    imageCard: {
        position: "relative",
        cursor: "pointer",
        "&:hover": {
            transform: "scale(1.02)",
            transition: "transform 0.2s ease",
        },
    },
    imageContainer: {
        position: "relative",
        width: "100%",
        height: "200px",
        overflow: "hidden",
        borderRadius: tokens.borderRadiusMedium,
    },
    image: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
    },
    groundboxOverlay: {
        position: "absolute",
        border: "2px solid #ff0000",
        backgroundColor: "rgba(255, 0, 0, 0.1)",
        pointerEvents: "none",
    },
    imageInfo: {
        ...shorthands.padding("10px"),
        display: "flex",
        flexDirection: "column",
        ...shorthands.gap("8px"),
    },
    badges: {
        display: "flex",
        flexWrap: "wrap",
        ...shorthands.gap("5px"),
    },
    stats: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        ...shorthands.padding("15px"),
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
    },
});

const GroundboxGallery = () => {
    const { apiClient } = useContext(Context);
    const [taskId, setTaskId] = useState(localStorage.getItem('groundboxGalleryTaskId') || '');
    const [galleryData, setGalleryData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        category: '',
        severity: '',
        hasGroundboxes: null,
        verified: null,
    });
    const [page, setPage] = useState(1);
    const [selectedImage, setSelectedImage] = useState(null);

    const styles = useStyles();

    const loadGalleryData = async () => {
        if (!taskId) return;
        
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                pageSize: '20',
            });
            
            if (filters.category) params.append('category', filters.category);
            if (filters.severity) params.append('severity', filters.severity);
            if (filters.hasGroundboxes !== null) params.append('hasGroundboxes', filters.hasGroundboxes.toString());
            if (filters.verified !== null) params.append('verified', filters.verified.toString());
            
            const response = await fetch(`http://localhost:5003/api/groundbox/gallery/${taskId}?${params}`, {
                headers: {
                    'Authorization': `Bearer ${apiClient.token}`
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                setGalleryData(data);
                localStorage.setItem('groundboxGalleryTaskId', taskId);
            } else {
                console.error('Failed to load gallery data');
                alert('加载图库数据失败');
            }
        } catch (error) {
            console.error('Failed to load gallery data:', error);
            alert('加载图库数据失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const renderGroundboxes = (image) => {
        if (!image.groundboxes || image.groundboxes.length === 0) return null;
        
        return image.groundboxes.map((groundbox, index) => {
            const bbox = groundbox.boundingBox.normalized;
            const color = getSeverityColor(groundbox.defect.severity);
            
            return (
                <div
                    key={index}
                    className={styles.groundboxOverlay}
                    style={{
                        left: `${bbox.x * 100}%`,
                        top: `${bbox.y * 100}%`,
                        width: `${bbox.width * 100}%`,
                        height: `${bbox.height * 100}%`,
                        borderColor: color,
                        backgroundColor: `${color}20`,
                    }}
                    title={`${groundbox.defect.category} - ${groundbox.defect.severity || 'unknown'}`}
                />
            );
        });
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'critical': return '#ff0000';
            case 'high': return '#ff6600';
            case 'medium': return '#ffcc00';
            case 'low': return '#00cc00';
            default: return '#666666';
        }
    };

    const getSeverityBadge = (severity) => {
        const colors = {
            critical: { background: '#ff0000', color: 'white' },
            high: { background: '#ff6600', color: 'white' },
            medium: { background: '#ffcc00', color: 'black' },
            low: { background: '#00cc00', color: 'white' },
        };
        
        const style = colors[severity] || { background: '#666666', color: 'white' };
        
        return (
            <Badge 
                style={style}
                size="small"
            >
                {severity || 'unknown'}
            </Badge>
        );
    };

    useEffect(() => {
        if (taskId) {
            loadGalleryData();
        }
    }, [taskId, filters, page]);

    const resetFilters = () => {
        setFilters({
            category: '',
            severity: '',
            hasGroundboxes: null,
            verified: null,
        });
        setPage(1);
    };

    return (
        <div className={styles.root}>
            <h2>Groundbox 标注图库</h2>
            
            {/* Controls */}
            <div className={styles.controls}>
                <div>
                    <Label>任务ID:</Label>
                    <Input
                        value={taskId}
                        onChange={(e) => setTaskId(e.target.value)}
                        placeholder="输入任务ID"
                        style={{ width: '200px' }}
                    />
                </div>
                
                <div>
                    <Label>缺陷类别:</Label>
                    <Input
                        value={filters.category}
                        onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                        placeholder="筛选类别"
                        style={{ width: '150px' }}
                    />
                </div>
                
                <div>
                    <Label>严重程度:</Label>
                    <Select 
                        value={filters.severity}
                        onChange={(e, data) => setFilters({ ...filters, severity: data.value })}
                        style={{ width: '120px' }}
                    >
                        <option value="">全部</option>
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                    </Select>
                </div>
                
                <div>
                    <Label>显示标注:</Label>
                    <Switch
                        checked={filters.hasGroundboxes === true}
                        onChange={(e, data) => setFilters({ 
                            ...filters, 
                            hasGroundboxes: data.checked ? true : null 
                        })}
                    />
                </div>
                
                <div>
                    <Label>已验证:</Label>
                    <Switch
                        checked={filters.verified === true}
                        onChange={(e, data) => setFilters({ 
                            ...filters, 
                            verified: data.checked ? true : null 
                        })}
                    />
                </div>
                
                <Button onClick={resetFilters}>
                    重置筛选
                </Button>
                
                <Button 
                    appearance="primary"
                    onClick={loadGalleryData}
                    disabled={loading || !taskId}
                    icon={<SearchRegular />}
                >
                    {loading ? '加载中...' : '搜索'}
                </Button>
            </div>
            
            {/* Statistics */}
            {galleryData && (
                <div className={styles.stats}>
                    <div>
                        <strong>统计信息:</strong>
                        <span style={{ marginLeft: '10px' }}>
                            总图片: {galleryData.statistics.totalImages} |
                            有标注: {galleryData.statistics.imagesWithGroundboxes} |
                            总标注框: {galleryData.statistics.totalGroundboxes} |
                            已验证: {galleryData.statistics.verifiedGroundboxes}
                        </span>
                    </div>
                    <div>
                        <strong>缺陷类别:</strong>
                        <span style={{ marginLeft: '10px' }}>
                            {galleryData.statistics.categoriesFound.join(', ') || '无'}
                        </span>
                    </div>
                </div>
            )}
            
            {/* Gallery Grid */}
            <div className={styles.gallery}>
                {galleryData?.images?.map((image) => (
                    <Card 
                        key={image.frameId}
                        className={styles.imageCard}
                        onClick={() => setSelectedImage(image)}
                    >
                        <CardPreview>
                            <div className={styles.imageContainer}>
                                <img
                                    src={image.imageUrl}
                                    alt={`Frame ${image.frameId}`}
                                    className={styles.image}
                                    loading="lazy"
                                />
                                {renderGroundboxes(image)}
                            </div>
                        </CardPreview>
                        
                        <CardHeader
                            header={
                                <div className={styles.imageInfo}>
                                    <div>
                                        <strong>Frame {image.frameId}</strong>
                                        <span style={{ marginLeft: '10px', fontSize: '12px', color: '#666' }}>
                                            {image.dimensions.width}x{image.dimensions.height}
                                        </span>
                                    </div>
                                    
                                    <div className={styles.badges}>
                                        <Badge color="informative" size="small">
                                            {image.summary.totalGroundboxes} 标注框
                                        </Badge>
                                        
                                        {image.summary.verifiedGroundboxes > 0 && (
                                            <Badge color="success" size="small">
                                                {image.summary.verifiedGroundboxes} 已验证
                                            </Badge>
                                        )}
                                        
                                        {image.summary.highestSeverity && getSeverityBadge(image.summary.highestSeverity)}
                                    </div>
                                    
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                        类别: {image.summary.categories.join(', ') || '无'}
                                    </div>
                                    
                                    {image.captureTime && (
                                        <div style={{ fontSize: '11px', color: '#999' }}>
                                            拍摄时间: {new Date(image.captureTime).toLocaleString()}
                                        </div>
                                    )}
                                </div>
                            }
                        />
                    </Card>
                ))}
            </div>
            
            {/* Pagination */}
            {galleryData?.pagination && galleryData.pagination.totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                    <Button 
                        disabled={page <= 1}
                        onClick={() => setPage(page - 1)}
                    >
                        上一页
                    </Button>
                    
                    <span style={{ alignSelf: 'center' }}>
                        第 {page} 页，共 {galleryData.pagination.totalPages} 页
                    </span>
                    
                    <Button 
                        disabled={page >= galleryData.pagination.totalPages}
                        onClick={() => setPage(page + 1)}
                    >
                        下一页
                    </Button>
                </div>
            )}
            
            {/* Image Detail Modal (you can implement this as needed) */}
            {selectedImage && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    zIndex: 1000,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer'
                }}
                onClick={() => setSelectedImage(null)}
                >
                    <div style={{
                        maxWidth: '90%',
                        maxHeight: '90%',
                        position: 'relative'
                    }}>
                        <img
                            src={selectedImage.imageUrl}
                            alt={`Frame ${selectedImage.frameId}`}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain'
                            }}
                        />
                        <div style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            backgroundColor: 'white',
                            padding: '10px',
                            borderRadius: '5px',
                            fontSize: '14px'
                        }}>
                            Frame {selectedImage.frameId} - {selectedImage.summary.totalGroundboxes} 标注框
                        </div>
                    </div>
                </div>
            )}
            
            {!galleryData && !loading && taskId && (
                <div style={{ textAlign: 'center', color: '#666', marginTop: '50px' }}>
                    <ImageRegular style={{ fontSize: '48px', marginBottom: '10px' }} />
                    <div>点击搜索按钮加载图库数据</div>
                </div>
            )}
        </div>
    );
};

export default GroundboxGallery;